/**
 * AgenticControlPlaneMap (F067)
 *
 * Live operational visual showing the LeftShield agentic control plane.
 * Flow: Signals → Policies → Decisions → Enforcement → Evidence → Outcomes
 *
 * Placement: Executive Command, Runtime Enforcement, Dashboard header
 */
import React from 'react';
import { makeStyles, tokens, Body2 } from '@fluentui/react-components';
import {
  CodeRegular,
  BranchForkRegular,
  PlayRegular,
  ShieldCheckmarkRegular,
  DocumentCheckmarkRegular,
  CertificateRegular,
  CheckmarkCircleRegular,
} from '@fluentui/react-icons';
import { governanceTokens } from './design-tokens';

export interface ControlPlaneSummary {
  repos: number;
  pullRequests: number;
  pipelines: number;
  policiesActive: number;
  evaluations: number;
  blocked: number;
  warned: number;
  allowed: number;
  evidenceGenerated: number;
  frameworkCoverage: number;
}

export interface AgenticControlPlaneMapProps {
  data: ControlPlaneSummary;
  compact?: boolean;
  onStageClick?: (stage: string) => void;
}

interface Stage {
  id: string;
  label: string;
  icon: React.ReactNode;
  value: string;
  color: string;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '0',
    overflowX: 'auto',
    padding: `${tokens.spacingVerticalM} 0`,
  },
  stage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    flex: '1 1 0',
    minWidth: '80px',
    cursor: 'pointer',
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  node: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase500,
    // Always-white icon-on-colour-circle contrast, regardless of theme —
    // the static-inverted token is the exact token equivalent of '#FFFFFF'.
    color: tokens.colorNeutralForegroundStaticInverted,
    position: 'relative',
    zIndex: 1,
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  value: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold as any,
    textAlign: 'center',
  },
  connector: {
    width: '32px',
    height: '2px',
    alignSelf: 'center',
    // -32px = spacingVerticalXXXL (32px), negated.
    marginTop: `calc(${tokens.spacingVerticalXXXL} * -1)`,
    flexShrink: 0,
  },
  arrow: {
    alignSelf: 'center',
    marginTop: `calc(${tokens.spacingVerticalXXXL} * -1)`,
    color: governanceTokens.colorPipelineConnector,
    fontSize: tokens.fontSizeBase300,
    flexShrink: 0,
  },
});

export const AgenticControlPlaneMap: React.FC<AgenticControlPlaneMapProps> = ({
  data,
  compact = false,
  onStageClick,
}) => {
  const styles = useStyles();

  const stages: Stage[] = [
    {
      id: 'signals',
      label: 'Signals',
      icon: <CodeRegular />,
      value: `${data.repos} repos`,
      color: governanceTokens.colorGovernanceSecondary,
    },
    {
      id: 'prs',
      label: 'Pull Requests',
      icon: <BranchForkRegular />,
      value: `${data.pullRequests}`,
      color: governanceTokens.colorGovernanceSecondary,
    },
    {
      id: 'pipelines',
      label: 'Pipelines',
      icon: <PlayRegular />,
      value: `${data.pipelines}`,
      color: governanceTokens.colorGovernanceSecondary,
    },
    {
      id: 'policies',
      label: 'Policies',
      icon: <DocumentCheckmarkRegular />,
      value: `${data.policiesActive} active`,
      color: governanceTokens.colorGovernancePrimary,
    },
    {
      id: 'enforcement',
      label: 'Enforcement',
      icon: <ShieldCheckmarkRegular />,
      value: `${data.evaluations} eval`,
      color: data.blocked > 0 ? governanceTokens.colorStatusError : governanceTokens.colorStatusSuccess,
    },
    {
      id: 'evidence',
      label: 'Evidence',
      icon: <CertificateRegular />,
      value: `${data.evidenceGenerated}`,
      color: governanceTokens.colorGovernancePrimary,
    },
    {
      id: 'outcomes',
      label: 'Outcomes',
      icon: <CheckmarkCircleRegular />,
      value: `${data.frameworkCoverage}%`,
      color: governanceTokens.colorStatusSuccess,
    },
  ];

  const displayStages = compact ? stages.filter(s => ['signals', 'policies', 'enforcement', 'evidence', 'outcomes'].includes(s.id)) : stages;

  return (
    <div className={styles.container}>
      {displayStages.map((stage, idx) => (
        <React.Fragment key={stage.id}>
          {idx > 0 && (
            <div
              className={styles.connector}
              style={{ backgroundColor: governanceTokens.colorPipelineConnector }}
            />
          )}
          <div
            className={styles.stage}
            onClick={() => onStageClick?.(stage.id)}
          >
            <div className={styles.node} style={{ backgroundColor: stage.color }}>
              {stage.icon}
            </div>
            <span className={styles.label}>{stage.label}</span>
            <Body2 className={styles.value} style={{ color: stage.color }}>
              {stage.value}
            </Body2>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};
